import { useSyncExternalStore } from "react";
import { en } from "./en";
import { fr } from "./fr";

/**
 * Tiny i18n store: the current language lives here, components read it with
 * useT() and re-render when it changes. Non-React code (lib/, format helpers)
 * reads it with getDict().
 *
 * en.ts is the source of truth; fr.ts must have the same shape, so a missing
 * translation is a TypeScript error.
 */

export type Lang = "fr" | "en";
export type Dict = typeof en;

export const LANGS: Lang[] = ["fr", "en"];
const DICTS: Record<Lang, Dict> = { fr, en };
const STORAGE_KEY = "vtc.lang";

/** Saved choice, else the browser language, else French. */
function detect(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "fr" || saved === "en") return saved;
  } catch {
    // Storage blocked: fall through to the browser language.
  }
  return navigator.language?.toLowerCase().startsWith("en") ? "en" : "fr";
}

let current: Lang = detect();
const listeners = new Set<() => void>();
document.documentElement.lang = current;

export function getLang(): Lang {
  return current;
}

export function getDict(): Dict {
  return DICTS[current];
}

export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  document.documentElement.lang = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Not remembered — still switches for this visit.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang);
}

/** The dictionary for the current language; re-renders on switch. */
export function useT(): Dict {
  return DICTS[useLang()];
}

/** Label for a data value (fuel, category…), falling back to the value itself. */
export function label(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}
